import { RouteRecordRaw } from 'vue-router'
/**
 * `meta.writes`: esta rota ALTERA alguma coisa, e é o nome da rota de leitura que a substitui
 * num ambiente encerrado — digitar o endereço de edição ali leva à visualização correspondente.
 *
 * Um campo só, e não dois: preenchido já quer dizer "esta rota altera", e diz para onde voltar.
 * Vazio é rota de leitura. Quem aplica é o `App.vue`, quando o ambiente termina de carregar.
 *
 * `meta.needs`: o PAPEL mínimo para esta rota. Os dois campos juntos respondem tudo, e cada um
 * responde uma coisa só:
 *
 * - com `writes`, a rota é conferida com `can` — o papel basta E o ambiente aceita alteração —,
 *   e quem não passa vai para a leitura que o `writes` nomeia;
 * - sem `writes`, é leitura restrita e vale `is`, só o papel: **o ambiente encerrado se lê
 *   inteiro**, então o desenho de um ambiente congelado continua abrindo para quem desenha;
 * - sem `needs`, qualquer membro entra, e é o caso das telas de uso.
 *
 * Toda rota com `writes` declara `needs`: é o que a checagem pura confere, porque uma rota de
 * alteração sem papel declarado passaria batida por todo mundo.
 *
 * `meta.approver`: esta rota é de ADMINISTRAÇÃO DO LUNEDI, e não de um ambiente — outra escada,
 * outro portão. Quem a aplica é o guarda global do `main.ts`, porque o `leaveIfDenied` só roda
 * dentro de um ambiente e uma rota de aplicação nunca passaria por ele.
 */
import Home from './components/home/Home.vue'
import Login from './components/home/Login.vue'
import System from './components/live/system/System.vue'
import SystemDesign from './components/design/system/SystemDesign.vue'
import ViewIdea from './components/design/class/ViewClass.vue'
import PropertyPage from './components/design/property/PropertyPage.vue'
import ViewInstance from './components/live/instance/ViewInstance.vue'
import CreateInstance from './components/live/instance/CreateInstance.vue'
import EditInstance from './components/live/instance/EditInstance.vue'
import ListInstances from './components/live/instance/ListInstances.vue'
import CollectionEntry from './components/live/instance/CollectionEntry.vue'
import CreateSystem from './components/design/system/CreateSystem.vue'
import EditSystem from './components/design/system/EditSystem.vue'
import CreateClass from "@/components/design/class/CreateClass.vue"
import EditClass from "@/components/design/class/EditClass.vue"
import Signup from "@/components/home/Signup.vue"
import ViewWorkflow from './components/design/workflow/ViewWorkflow.vue'
import Kanban from './components/live/instance/Kanban.vue'
import RelationshipKanban from './components/live/instance/RelationshipKanban.vue'
import CreateWorkflow from './components/design/workflow/CreateWorkflow.vue'
import EditWorkflow from './components/design/workflow/EditWorkflow.vue'
import ViewDomain from './components/design/domain/ViewDomain.vue'
import CreateDomain from './components/design/domain/CreateDomain.vue'
import EditDomain from './components/design/domain/EditDomain.vue'
import Invitations from './components/account/Invitations.vue'
import Profile from './components/account/Profile.vue'

const routes: RouteRecordRaw[] = [
    {
        name: 'home',
        path: '/',
        component: Home,
        meta: {}
    },
    {
        name: 'login',
        path: '/login',
        component: Login,
        meta: { public: true }
    },
    {
        name: 'signup',
        path: '/signup',
        component: Signup,
        meta: { public: true }
    },
    {
        name: 'view-system',
        path: '/workspaces/:system',
        component: System,
        meta: {}
    },
    {
        //A URL da coleção não desenha nada: manda para a visão certa. É para cá que
        //apontam a aba da coleção e o degrau do caminho, e é o que torna
        ///collections/:idea um endereço estável para compartilhar.
        name: 'collection',
        path: '/workspaces/:system/collections/:idea',
        component: CollectionEntry,
        meta: {}
    },
    {
        //O caminho diz qual VISÃO é, e não "a listagem": lista e Kanban são irmãs, e
        //quem responde por "a coleção" é a rota acima.
        name: 'list-instances',
        path: '/workspaces/:system/collections/:idea/grid',
        component: ListInstances,
        meta: {}
    },
    {
        name: 'kanban',
        path: '/workspaces/:system/collections/:idea/kanban',
        component: Kanban,
        meta: {}
    },
    {
        //Kanban dos itens de UM relacionamento de UMA instância. Precisa de rota
        //própria: a do kanban da coleção mostraria todos os itens, não só os desta.
        name: 'relationship-kanban',
        path: '/workspaces/:system/collections/:idea/items/:instance/relationships/:property/kanban',
        component: RelationshipKanban,
        meta: {}
    },
    {
        name: 'view-instance',
        path: '/workspaces/:system/collections/:idea/items/:instance',
        component: ViewInstance,
        meta: {}
    },
    {
        name: 'edit-instance',
        path: '/workspaces/:system/collections/:idea/items/:instance/edit',
        component: EditInstance,
        meta: { needs: 'editor', writes: 'view-instance' }
    },
    {
        name: 'create-instance',
        path: '/workspaces/:system/collections/:idea/create-item',
        component: CreateInstance,
        meta: { needs: 'editor', writes: 'list-instances' }
    },
    {
        //Tela de aplicação, e não de ambiente: `needs` não serve aqui, porque quem o aplica é o
        //`leaveIfDenied`, que só roda dentro de um ambiente. `approver` é lido pelo guarda
        //global do main.ts, que é o único que roda em toda rota.
        //Os dados da própria pessoa. Sem `approver`: todo mundo tem perfil, e o que ele mostra
        //já é de quem está olhando
        name: 'profile',
        path: '/profile',
        component: Profile,
        meta: {}
    },
    {
        name: 'invitations',
        path: '/invitations',
        component: Invitations,
        meta: { approver: true }
    },
    {
        name: 'create-system',
        path: '/create-workspace/',
        component: CreateSystem,
    },
    {
        name: 'edit-system',
        path: '/workspaces/:system/edit',
        component: EditSystem,
        //Renomear o ambiente é do ADMINISTRADOR, e é a única rota de design que não basta
        //ser designer: o ambiente é dele, o desenho é do designer
        meta: { needs: 'admin', writes: 'design-system' }
    },
    {
        name: 'design-system',
        path: '/workspaces/:system/design',
        component: SystemDesign,
        meta: { needs: 'designer' }
    },
    {
        //Os acessos eram uma tela, o terceiro modo; viraram a aba Membros do design do ambiente
        //(pedido do usuário). O endereço fica, para os guardados, e leva à aba
        path: '/workspaces/:system/access',
        redirect: to => ({name: 'design-system', params: to.params, query: {tab: 'members'}})
    },
    {
        name: 'create-idea',
        path: '/workspaces/:system/create-collection',
        component: CreateClass,
        meta: { needs: 'designer', writes: 'design-system' }
    },
    {
        name: 'create-workflow',
        path: '/workspaces/:system/create-workflow',
        component: CreateWorkflow,
        meta: { needs: 'designer', writes: 'design-system' }
    },
    {
        //Era a URL nua da coleção; saiu para ela poder significar "a coleção", e não
        //"o desenho da coleção".
        name: 'view-idea',
        path: '/workspaces/:system/collections/:idea/design',
        component: ViewIdea,
        meta: { needs: 'designer' }
    },
    {
        name: 'view-workflow',
        path: '/workspaces/:system/workflows/:workflow',
        component: ViewWorkflow,
        meta: { needs: 'designer' }
    },
    {
        name: 'edit-idea',
        path: '/workspaces/:system/collections/:idea/edit',
        component: EditClass,
        meta: { needs: 'designer', writes: 'view-idea' }
    },
    {
        name: 'edit-workflow',
        path: '/workspaces/:system/workflows/:workflow/edit',
        component: EditWorkflow,
        meta: { needs: 'designer', writes: 'view-workflow' }
    },
    {
        name: 'create-domain',
        path: '/workspaces/:system/create-domain',
        component: CreateDomain,
        meta: { needs: 'designer', writes: 'design-system' }
    },
    {
        name: 'view-domain',
        path: '/workspaces/:system/domains/:domain',
        component: ViewDomain,
        meta: { needs: 'designer' }
    },
    {
        name: 'edit-domain',
        path: '/workspaces/:system/domains/:domain/edit',
        component: EditDomain,
        meta: { needs: 'designer', writes: 'view-domain' }
    },
    //A página da propriedade, para onde o Expandir da janela leva (pedido do usuário): o mesmo editor da
    //janela (`PropertyEditor`), uma página para criar e editar, das três espécies
    {
        name: 'create-property',
        path: '/workspaces/:system/collections/:idea/create-property',
        component: PropertyPage,
        meta: { needs: 'designer', writes: 'view-idea' }
    },
    {
        name: 'edit-property',
        path: '/workspaces/:system/collections/:idea/properties/:property',
        component: PropertyPage,
        meta: { needs: 'designer', writes: 'view-idea' }
    },
    //As páginas de criar e editar propriedade, de antes da janela: ninguém mais navegava para elas, e o
    //endereço antigo leva ao design da coleção, onde a janela abre. Eram seis cópias do que a janela faz,
    //sobre editores que passaram a editar os dois lados da associação
    ...[
        '/workspaces/:system/collections/:idea/create-attribute',
        '/workspaces/:system/collections/:idea/attributes/:property',
        '/workspaces/:system/collections/:idea/create-relationship',
        '/workspaces/:system/collections/:idea/relationships/:property',
        '/workspaces/:system/collections/:idea/create-reverse-relationship/collections/:otherIdea/relationships/:otherProperty',
        '/workspaces/:system/class/:idea/reverse-relationships/:property'
    ].map((path): RouteRecordRaw => ({
        path,
        redirect: to => ({name: 'view-idea', params: {system: to.params.system, idea: to.params.idea}})
    }))
]

//Sandbox do prazo de itens terminados: dados fabricados, sem backend e sem login.
//O import dinâmico dentro do if faz o webpack descartar a página no build de
//produção, onde NODE_ENV é inlined e o ramo inteiro vira código morto.
if (process.env.NODE_ENV === 'development') {
    routes.push({
        name: 'dev-kanban-sandbox',
        path: '/dev/kanban',
        component: () => import('./components/dev/KanbanSandbox.vue'),
        meta: { public: true }
    })
}

export default routes
